/**
 * Choose a password, from an emailed link (FS-012).
 *
 * One page, two doors: `/accept-invite` for someone joining, `/reset-password`
 * for someone who forgot. Same mechanics, different words.
 *
 * **The token is taken out of the address bar on mount**, as the banking page
 * does: the fragment kept it off every server, but it still sits in the address
 * bar, in history, and in anything the person copies from there. It is kept in
 * a ref, and only copied in when the ref is empty, so React's development
 * double-mount — which runs the effect a second time after the address bar has
 * been wiped — does not lose it.
 *
 * **The link is checked before the person types anything.** Finding out a link
 * has expired *after* choosing a password is the kind of small insult that
 * makes people stop trusting a system.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/api/client";
import AuthShell from "@/components/auth/AuthShell";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useAuthStore } from "@/stores/authStore";

export type SetPasswordMode = "invitation" | "reset";

interface Inspection {
  purpose: "invitation" | "password_reset";
  first_name: string;
  email: string;
  organization_name: string;
}

/** Mirrors `core/password_policy.py`. The server is the judge; this is a hint. */
const MIN_LENGTH = 10;

const ENDPOINT: Record<SetPasswordMode, string> = {
  invitation: "/api/v1/auth/invitations/accept",
  reset: "/api/v1/auth/password/reset",
};

function detailOf(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function SetPasswordPage({ mode }: { mode: SetPasswordMode }) {
  const navigate = useNavigate();
  const token = useRef("");
  const [loading, setLoading] = useState(true);
  const [who, setWho] = useState<Inspection | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fragment = window.location.hash.replace(/^#/, "").trim();
    if (fragment && !token.current) token.current = fragment;
    if (fragment) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
    if (!token.current) {
      setDead(
        "This page needs the link from your email. Open the email again and use the button in it.",
      );
      setLoading(false);
      return;
    }
    apiClient
      .post<Inspection>("/api/v1/auth/tokens/inspect", {
        token: token.current,
      })
      .then((resp) => {
        const expected =
          mode === "invitation" ? "invitation" : "password_reset";
        if (resp.data.purpose !== expected) {
          setDead(
            "This link is for a different step. Open the most recent email and use the button in it.",
          );
        } else {
          setWho(resp.data);
        }
      })
      .catch((err) =>
        setDead(detailOf(err, "This link can't be used. Ask for a new one.")),
      )
      .finally(() => setLoading(false));
  }, [mode]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await apiClient.post<{ access_token: string }>(
        ENDPOINT[mode],
        { token: token.current, new_password: password },
      );
      const store = useAuthStore.getState();
      store.adoptToken(resp.data.access_token);
      await store.fetchUser();
      navigate("/", { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 410) {
        setDead(detailOf(err, "This link can't be used. Ask for a new one."));
      } else {
        setError(detailOf(err, "That didn't work. Try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const invitation = mode === "invitation";

  if (loading) {
    return (
      <AuthShell title={invitation ? "Welcome" : "Choose a new password"}>
        <LoadingSpinner className="py-6" />
      </AuthShell>
    );
  }

  if (dead || !who) {
    return (
      <AuthShell
        title={invitation ? "Invitation link" : "Password reset link"}
        footer={
          <Link
            to="/login"
            className="font-medium text-brand-600 hover:text-brand-700"
          >
            Back to sign in
          </Link>
        }
      >
        <div className="space-y-4 text-sm">
          <p className="text-[var(--text-primary)]">{dead}</p>
          {invitation ? (
            <p className="text-[var(--text-secondary)]">
              Invitations last seven days. Ask whoever invited you to send a new
              one.
            </p>
          ) : (
            <Button
              className="w-full"
              variant="secondary"
              onClick={() => navigate("/forgot-password")}
            >
              Send me a new link
            </Button>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={
        invitation ? `Welcome, ${who.first_name}` : "Choose a new password"
      }
      subtitle={
        invitation ? (
          <>
            Choose a password to finish setting up your{" "}
            <span className="font-medium text-[var(--text-primary)]">
              {who.organization_name}
            </span>{" "}
            account.
          </>
        ) : (
          <>
            For{" "}
            <span className="font-medium text-[var(--text-primary)]">
              {who.email}
            </span>
            . Every device signed in with the old password will be signed out.
          </>
        )
      }
    >
      <form onSubmit={submit} className="space-y-5">
        {error && (
          <div
            role="alert"
            className="rounded-[var(--radius-lg)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)]"
          >
            {error}
          </div>
        )}
        {/* For password managers: which account this password belongs to. */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={who.email}
          readOnly
          hidden
        />
        <div>
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            autoComplete="new-password"
          />
          <p
            className={`mt-1.5 text-xs ${
              password.length >= MIN_LENGTH
                ? "text-[var(--text-success)]"
                : "text-[var(--text-tertiary)]"
            }`}
          >
            At least {MIN_LENGTH} characters. A short phrase is easier to
            remember than symbols.
          </p>
        </div>
        <Input
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          autoComplete="new-password"
        />
        <Button
          type="submit"
          className="w-full"
          size="lg"
          isLoading={submitting}
        >
          {invitation ? "Set password and sign in" : "Save and sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default SetPasswordPage;
